import { useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import PageFooter from '@/pages/home/components/PageFooter';

const freeFeatures = [
  'Full access to the 3-hour summit',
  'Live AI demonstrations',
  'Expert-led sessions (4 speakers)',
  'Participation in live Q&A',
  'Access to essential resources',
  'AI Starter Toolkit',
  'Prompt Library',
  'Replay access',
];

const premiumFeatures = [
  'Everything in Free Access',
  'Certificate of Participation',
  'Access to GNA AI Business Community',
  'Discounted AI Certification Exams (i3Cert)',
  'Exclusive Post-Summit Implementation Session',
  'Priority access to AI consulting',
];

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    country: '',
    access: 'free',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  
    if (e.target.name === "email") {
      setAlreadyRegistered(false);
      setOtpSent(false);
      setVerified(false);
    }
  };

  const sendOTP = async () => {
    if (!formData.email) {
      alert("Enter email first");
      return;
    }
  
    setOtpLoading(true);
  
    try {
      const res = await fetch("/api/event/send-otp/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email }),
      });
  
      const data = await res.json();
  
      if (!res.ok) {
        if (data.error === "User already registered") {
          setAlreadyRegistered(true);   // 🔥 ADD THIS
          alert("You are already registered. Please check your email.");
          return;
        }
        throw new Error(data.error || "Failed to send OTP");
      }
  
      setOtpSent(true);
      alert("OTP sent to your email");
  
    } catch (err) {
      alert(err.message);
    } finally {
      setOtpLoading(false);
    }
  };


  const verifyOTP = async () => {
    if (!otp) {
      alert("Enter OTP");
      return;
    }
  
    try {
      const res = await fetch("/api/event/verify-otp/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          code: otp,
        }),
      });
  
      const data = await res.json();
  
      if (!res.ok) throw new Error(data.error || "Invalid OTP");
  
      setVerified(true);
      alert("Email verified");
  
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!verified) {
      alert("Please verify your email first");
      return;
    }

    setLoading(true);
    try {
      
      const res = await fetch('/api/event/register/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify({
          full_name: formData.name,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          business_type: formData.business,
          access: formData.access,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Registration failed");
      }

      if (data.access === "free") {
        setSubmitted(true);
      } else {
        // PREMIUM → redirect to payment
        localStorage.setItem("email", formData.email); 
        // call backend to create Stripe session
        const checkoutRes = await fetch("/api/event/create-checkout-session/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email: formData.email }),
        });

        const checkoutData = await checkoutRes.json();

        if (checkoutData.url) {
          window.location.href = checkoutData.url; // ✅ GO TO STRIPE
        } else {
          alert("Payment initialization failed");
        }
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Page Header */}
      <div className="bg-gradient-to-br from-green-800 to-green-900 pt-28 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-green-200 text-xs font-semibold uppercase tracking-widest mb-4">
            Registration
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
            Secure Your Spot
          </h1>
          <p className="text-green-200/80 text-base md:text-lg max-w-xl mx-auto">
            Join thousands of business owners and entrepreneurs at the GNA AI Business Summit — May 30th, 2026.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <i className="ri-calendar-line text-amber-400"></i>
              <span className="text-white text-sm font-semibold whitespace-nowrap">May 30th, 2026</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <i className="ri-time-line text-amber-400"></i>
              <span className="text-white text-sm font-semibold whitespace-nowrap">12:30 PM – 2:30 PM CST</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-600/40 border border-green-400/40">
              <i className="ri-ticket-line text-green-300"></i>
              <span className="text-green-200 text-sm font-semibold whitespace-nowrap">FREE to Attend</span>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* Ticket Options */}
          <div className="w-full lg:w-[420px] space-y-5">
            {/* Free Ticket */}
            <div
              onClick={() => setFormData({ ...formData, access: 'free' })}
              className={`rounded-2xl border-2 p-6 cursor-pointer transition-all duration-200 ${
                formData.access === 'free'
                  ? 'border-green-600 bg-green-50'
                  : 'border-gray-200 bg-white hover:border-green-300'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.access === 'free' ? 'border-green-600 bg-green-600' : 'border-gray-300'}`}>
                    {formData.access === 'free' && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <span className="font-bold text-gray-900 text-base">Free Access</span>
                </div>
                <span className="text-2xl font-extrabold text-gray-900">$0</span>
              </div>
              <ul className="space-y-2">
                {freeFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 text-sm mt-0.5 shrink-0"></i>
                    <span className="text-gray-600 text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Premium Ticket */}
            <div
              onClick={() => setFormData({ ...formData, access: 'premium' })}
              className={`rounded-2xl border-2 p-6 cursor-pointer transition-all duration-200 relative ${
                formData.access === 'premium'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-200 bg-white hover:border-amber-300'
              }`}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 rounded-full bg-gradient-to-r from-green-700 to-green-800 text-white text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">
                  Recommended
                </span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.access === 'premium' ? 'border-amber-500 bg-amber-500' : 'border-gray-300'}`}>
                    {formData.access === 'premium' && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <span className="font-bold text-gray-900 text-base">Premium Access</span>
                </div>
                <span className="text-2xl font-extrabold text-green-700">$100</span>
              </div>
              <ul className="space-y-2">
                {premiumFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                    <span className="text-gray-600 text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Registration Form */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 mb-6">
                  <i className="ri-check-double-line text-green-700 text-4xl"></i>
                </div>
                <h3 className="text-gray-900 font-extrabold text-2xl mb-3">You're Registered!</h3>
                <p className="text-gray-500 text-base max-w-sm">
                  {formData.access === 'premium'
                    ? 'Thank you for choosing Premium! Check your email for payment and confirmation details.'
                    : 'Check your email for confirmation details. We\'ll see you on May 30th, 2026!'}
                </p>
                <div className="mt-6 flex items-center gap-2 px-5 py-3 rounded-full bg-green-50 border border-green-200">
                  <i className="ri-calendar-check-line text-green-700"></i>
                  <span className="text-green-700 text-sm font-semibold">May 30th, 2026 · 12:30 PM CST</span>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-xl font-extrabold text-gray-900 mb-1">Complete Your Registration</h2>
                  <p className="text-gray-500 text-sm">
                    Registering for: <strong className="text-green-700">{formData.access === 'premium' ? 'Premium Access ($100)' : 'Free Access ($0)'}</strong>
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <input type="hidden" name="access" value={formData.access} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-600 focus:bg-white transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Email Address *
                      </label>

                      <div className="flex gap-2">
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm"
                        />

                        <button
                          type="button"
                          onClick={sendOTP}
                          disabled={otpLoading || alreadyRegistered}
                          className="px-4 py-2 bg-green-700 text-white rounded-xl text-sm"
                        >
                          {otpLoading ? "Sending..." : "Send Code"}
                        </button>
                      </div>
                    </div>
                  </div>
                  {alreadyRegistered && (
                    <p className="text-red-500 text-sm mt-1">
                      ⚠️ You are already registered. Check your email.
                    </p>
                  )}

                  {otpSent && !verified && (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        placeholder="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        className="flex-1 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm"
                      />

                      <button
                        type="button"
                        onClick={verifyOTP}
                        disabled={alreadyRegistered}
                        className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm"
                      >
                        Verify
                      </button>
                    </div>
                  )}

                  {verified && (
                    <p className="text-green-600 text-sm mt-1">
                      ✅ Email verified
                    </p>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (000) 000-0000"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-600 focus:bg-white transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Country *</label>
                      <input
                        type="text"
                        name="country"
                        required
                        value={formData.country}
                        onChange={handleChange}
                        placeholder="Your country"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-600 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Business Type *</label>
                    <select
                      name="business"
                      required
                      value={formData.business}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:border-green-600 focus:bg-white transition-colors cursor-pointer"
                    >
                      <option value="">Select your business type</option>
                      <option value="SME / Small Business">SME / Small Business</option>
                      <option value="Startup">Startup</option>
                      <option value="Freelancer / Consultant">Freelancer / Consultant</option>
                      <option value="Corporate / Enterprise">Corporate / Enterprise</option>
                      <option value="Non-Profit / NGO">Non-Profit / NGO</option>
                      <option value="Student / Aspiring Entrepreneur">Student / Aspiring Entrepreneur</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !verified || alreadyRegistered}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-green-700 to-green-800 text-white font-bold text-base hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {loading
                      ? 'Registering...'
                      : formData.access === 'premium'
                      ? 'Register for Premium – $100 →'
                      : 'Register for Free →'}
                  </button>

                  <p className="text-center text-gray-400 text-xs">
                    {formData.access === 'premium'
                      ? 'You will be redirected to secure payment after registration'
                      : 'No credit card required · Instant confirmation'}
                  </p>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Trust Signals */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {[
            { icon: 'ri-shield-check-line', title: 'Secure Registration', desc: 'Your information is safe and will never be shared with third parties.' },
            { icon: 'ri-global-line', title: 'Open to Everyone', desc: 'Participants from any country are welcome to join this global summit.' },
            { icon: 'ri-video-line', title: 'Replay Available', desc: 'Can\'t make it live? All registered participants get replay access.' },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-gray-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-100 shrink-0">
                <i className={`${item.icon} text-green-700 text-lg`}></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">{item.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </main>

      <PageFooter />
    </div>
  );
};

export default RegisterPage;
